/**
 * The toon pipeline (ART-BIBLE §2, §3, §4).
 *
 * One patched MeshToonMaterial program family covers the whole game:
 *   · coloured gradient ramp   (getGradientIrradiance patched to return .rgb)
 *   · one fixed rim light      (moonlight through a window — never animated)
 *   · screen-space halftone    (only inside the darkest band, faded by distance)
 *   · procedural grime         (mandatory on chassis — a clean cabinet is a bug)
 *   · instanced tint/emissive  (hundreds of tokens, one draw call)
 *   · in-shader tag signatures (RIM + FACE channels; AURA lives in the FX pool)
 *
 * Everything animated by a single shared uTime uniform: one write per frame.
 */

import {
  MeshToonMaterial, Color, Vector3, Vector2, FrontSide, DoubleSide,
  type Texture, type Side, type Material, type WebGLProgramParametersWithUniforms,
} from 'three';
import { ramps, type RampName } from './ramps';
import { RIM, HALFTONE } from './palette';

/** shared clock — updated once per frame by the stage */
export const uTime = { value: 0 };
/** shared screen-space scale for constant-width outlines: 2*tan(fov/2)/pixelHeight */
export const uPxScale = { value: 0.002 };
/** the key light's direction in VIEW space — lets the halftone find the true shadow band */
export const uKeyDir = { value: new Vector3(-0.5, 0.7, 0.5).normalize() };

/** the room's fixed sky direction in view space (up + camera-left) */
const RIM_DIR = new Vector3(-0.45, 0.72, 0.52).normalize();

export const TAG_BITS = {
  Magnet: 0, Geist: 1, Gummi: 2, Kleber: 3, Spalt: 4, Gold: 5,
  Fluch: 6, Feuer: 7, Gluecks: 8, Schwer: 9, Zins: 10, Echo: 11,
} as const;

export interface ToonOpts {
  color?: number;
  ramp?: RampName;
  rim?: number;
  rimColor?: number;
  /** 0 = off; chassis default 1 */
  halftone?: number;
  halftonePitch?: number;
  emissive?: number;
  emissiveIntensity?: number;
  grime?: Texture | null;
  grimeStrength?: number;
  map?: Texture | null;
  instanced?: boolean;
  /** in-shader token tag signatures — implies instanced */
  tokenFx?: boolean;
  transparent?: boolean;
  opacity?: number;
  side?: Side;
  flatShading?: boolean;
  depthWrite?: boolean;
  name?: string;
}

export interface ToonMaterial extends MeshToonMaterial {
  sph: {
    rim: { value: number };
    halftone: { value: number };
    grimeStrength: { value: number };
    fx: { value: number };
  };
}

const GLSL_BIT = /* glsl */`
float sphBit( float mask, float i ) { return mod( floor( mask / exp2( i ) ), 2.0 ); }
`;

export function toon(opts: ToonOpts = {}): ToonMaterial {
  const instanced = !!(opts.instanced || opts.tokenFx);
  const fx = !!opts.tokenFx;

  const m = new MeshToonMaterial({
    color: opts.color ?? 0xffffff,
    gradientMap: ramps()[opts.ramp ?? 'HARD3'],
    map: opts.map ?? null,
    emissive: new Color(opts.emissive ?? 0x000000),
    transparent: !!opts.transparent,
    opacity: opts.opacity ?? 1,
    side: opts.side ?? FrontSide,
  }) as ToonMaterial;
  if (opts.flatShading) (m as unknown as { flatShading: boolean }).flatShading = true;
  if (opts.depthWrite !== undefined) m.depthWrite = opts.depthWrite;
  if (opts.name) m.name = opts.name;
  // the grime patch samples vUv; without any uv-using map three would not declare it
  if (opts.grime) m.defines = { ...(m.defines ?? {}), USE_UV: '' };

  const sph = {
    rim: { value: opts.rim ?? 0.35 },
    halftone: { value: opts.halftone ?? 0 },
    grimeStrength: { value: opts.grimeStrength ?? 0 },
    fx: { value: fx ? 1 : 0 },
  };
  m.sph = sph;

  const rimColor = new Color(opts.rimColor ?? RIM);
  const halfColor = new Color(HALFTONE);
  const pitch = { value: opts.halftonePitch ?? 4.0 };
  const grimeTex = { value: opts.grime ?? null };

  m.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uTime = uTime;
    shader.uniforms.uPxScale = uPxScale;
    shader.uniforms.uRimColor = { value: rimColor };
    shader.uniforms.uRimStrength = sph.rim;
    shader.uniforms.uRimDir = { value: RIM_DIR };
    shader.uniforms.uKeyDir = uKeyDir;
    shader.uniforms.uHalftone = sph.halftone;
    shader.uniforms.uHalfPitch = pitch;
    shader.uniforms.uHalfColor = { value: halfColor };
    shader.uniforms.uGrime = grimeTex;
    shader.uniforms.uGrimeStrength = sph.grimeStrength;
    shader.uniforms.uFx = sph.fx;

    // ---- 1. coloured ramp: the single most important line in the kit -------
    shader.fragmentShader = shader.fragmentShader.replace(
      'return vec3( texture2D( gradientMap, coord ).r );',
      'return texture2D( gradientMap, coord ).rgb;',
    );

    // ---- 2. vertex: instance attributes + local coords for tag FX ----------
    let vsHead = /* glsl */`
      varying vec3 vSphLocal;
      varying vec3 vSphLocalN;
      varying float vSphSize;
      uniform float uPxScale;
    `;
    if (instanced) {
      vsHead += /* glsl */`
        attribute vec3 aTint;
        attribute vec3 aEmi;
        attribute vec3 aPip;
        attribute float aTag;
        attribute float aPhase;
        varying vec3 vTint; varying vec3 vEmi; varying vec3 vPip;
        varying float vTag; varying float vPhase;
      `;
    }
    shader.vertexShader = vsHead + shader.vertexShader;

    let vsBody = /* glsl */`
      vSphLocal = position;
      vSphLocalN = normal;
      vSphSize = 2.0 * length( position ) / max( 1e-4, uPxScale * max( 0.001, - mvPosition.z ) );
    `;
    if (instanced) {
      vsBody += 'vTint = aTint; vEmi = aEmi; vPip = aPip; vTag = aTag; vPhase = aPhase;';
    }
    // project_vertex is where mvPosition exists
    shader.vertexShader = shader.vertexShader.replace(
      '#include <project_vertex>',
      '#include <project_vertex>\n' + vsBody,
    );

    // ---- 3. fragment head --------------------------------------------------
    let fsHead = /* glsl */`
      uniform float uTime;
      uniform vec3 uRimColor;
      uniform float uRimStrength;
      uniform vec3 uRimDir;
      uniform vec3 uKeyDir;
      uniform float uHalftone;
      uniform float uHalfPitch;
      uniform vec3 uHalfColor;
      uniform sampler2D uGrime;
      uniform float uGrimeStrength;
      uniform float uFx;
      varying vec3 vSphLocal;
      varying vec3 vSphLocalN;
      varying float vSphSize;
      ${GLSL_BIT}
    `;
    if (instanced) {
      fsHead += /* glsl */`
        varying vec3 vTint; varying vec3 vEmi; varying vec3 vPip;
        varying float vTag; varying float vPhase;
      `;
    }
    shader.fragmentShader = fsHead + shader.fragmentShader;

    // ---- 4. body colour: instance tint, grime, tag FACE/RIM signatures -----
    let colorPatch = '';
    if (instanced) {
      colorPatch += 'diffuseColor.rgb *= vTint;\n';
    }
    if (opts.grime) {
      colorPatch += /* glsl */`
        {
          float g = texture2D( uGrime, vUv ).r;
          diffuseColor.rgb *= mix( 1.0, g, uGrimeStrength );
        }
      `;
    }
    if (fx) {
      colorPatch += /* glsl */`
      if ( uFx > 0.5 ) {
        float r    = clamp( length( vSphLocal.xy ) / 0.5, 0.0, 1.4 );
        float ang  = atan( vSphLocal.y, vSphLocal.x );
        float face = step( 0.55, abs( normalize( vSphLocalN ).z ) );
        float edge = 1.0 - face;
        float t    = uTime + vPhase * 6.2831853;

        // --- FACE channel -------------------------------------------------
        // [Fluch] — the one tag allowed to touch body colour: violet smolder
        float bFluch = sphBit( vTag, 6.0 );
        diffuseColor.rgb = mix( diffuseColor.rgb,
          mix( diffuseColor.rgb * 0.55, vec3( 0.42, 0.20, 0.62 ), 0.45 ), bFluch );

        // [Gold] — a specular streak sweeps the face every 2.2s, staggered
        float bGold = sphBit( vTag, 5.0 );
        if ( bGold > 0.5 ) {
          float sweep = ( vSphLocal.x * 1.6 + vSphLocal.y * 0.9 ) * 2.4 - mod( t * 0.9, 6.2831853 ) + 3.0;
          float band  = smoothstep( 0.86, 1.0, cos( sweep ) ) * face;
          diffuseColor.rgb += vec3( 1.0, 0.86, 0.45 ) * band * 0.55;
        }

        // [Spalt] — hairline crack with a lit interior; the crack IS the seam
        float bSpalt = sphBit( vTag, 4.0 );
        if ( bSpalt > 0.5 ) {
          float k = vPhase * 3.14159;
          float d = abs( vSphLocal.x * cos( k ) + vSphLocal.y * sin( k ) );
          float crack = ( 1.0 - smoothstep( 0.006, 0.022, d ) ) * step( r, 1.02 );
          diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.02, 0.05, 0.07 ), crack * 0.85 * face );
          diffuseColor.rgb += vec3( 0.18, 0.91, 1.0 ) * crack * face * 0.5;
        }

        // [Gluecks] — soft green halo at the coin's edge + a clover pip
        float bGlueck = sphBit( vTag, 8.0 );
        if ( bGlueck > 0.5 ) {
          float halo = smoothstep( 0.62, 0.98, r ) * face;
          diffuseColor.rgb += vec3( 0.28, 0.90, 0.34 ) * halo * ( 0.30 + 0.14 * sin( t * 2.0 ) );
        }

        // --- RIM channel --------------------------------------------------
        // [Magnet] — two horseshoe nubs at 0 and PI
        float bMag = sphBit( vTag, 0.0 );
        if ( bMag > 0.5 ) {
          float nub = smoothstep( 0.80, 0.96, abs( cos( ang ) ) ) * edge;
          diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.10, 0.55, 0.68 ), nub * 0.8 );
          diffuseColor.rgb += vec3( 0.18, 0.91, 1.0 ) * nub * ( 0.35 + 0.25 * sin( t * 6.0 ) );
        }
        // [Zins] — raised amber bezel that brightens as interest accrues
        float bZins = sphBit( vTag, 10.0 );
        diffuseColor.rgb += vec3( 1.0, 0.70, 0.24 ) * edge * bZins * 0.45;
        // [Schwer] — the rim goes dark and heavy (outline widens on the CPU side)
        float bSchwer = sphBit( vTag, 9.0 );
        diffuseColor.rgb *= mix( 1.0, 0.55, edge * bSchwer );
        // [Gummi] — fat, glossy orange rim
        float bGummi = sphBit( vTag, 2.0 );
        diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.95, 0.46, 0.18 ), edge * bGummi * 0.75 );

        // --- degraded tags become a pip at 4 o'clock ------------------------
        if ( dot( vPip, vPip ) > 0.001 ) {
          vec2 pipC = vec2( cos( -1.0472 ), sin( -1.0472 ) ) * 0.40;
          float pd = length( vSphLocal.xy - pipC );
          float pip = ( 1.0 - smoothstep( 0.055, 0.075, pd ) ) * face;
          diffuseColor.rgb = mix( diffuseColor.rgb, vPip, pip * 0.9 );
        }

        // [Geist] — translucent body (AURA wisps live in the FX pool)
        float bGeist = sphBit( vTag, 1.0 );
        diffuseColor.a *= mix( 1.0, 0.45, bGeist );
      }
      `;
    }
    if (colorPatch) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n' + colorPatch,
      );
    }

    // ---- 5. instance emissive ---------------------------------------------
    if (instanced) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vEmi;',
      );
    }

    // ---- 6. rim light + halftone ------------------------------------------
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      /* glsl */`
      {
        vec3 shN = normalize( normal );
        vec3 shV = normalize( vViewPosition );
        float fres = pow( 1.0 - clamp( dot( shN, shV ), 0.0, 1.0 ), 3.2 );
        float rimFace = max( dot( shN, uRimDir ), 0.0 );
        outgoingLight += uRimColor * ( fres * rimFace * uRimStrength );

        // Halftone lives ONLY inside the darkest ramp band. Finding that band from
        // luminance is a lie (dark paint reads as shadow); we test the real terminator.
        if ( uHalftone > 0.001 ) {
          float dnl = dot( shN, uKeyDir );
          float sh  = 1.0 - smoothstep( -0.06, 0.10, dnl );
          float depth = length( vViewPosition );
          float dfade = 1.0 - smoothstep( 80.0, 150.0, depth );
          float sfade = smoothstep( 40.0, 90.0, vSphSize );
          float amt = sh * dfade * sfade * uHalftone;
          if ( amt > 0.001 ) {
            float ca = 0.2588, sa = 0.9659;   // 15 degrees
            vec2 sp = gl_FragCoord.xy;
            vec2 rp = vec2( sp.x * sa - sp.y * ca, sp.x * ca + sp.y * sa ) / uHalfPitch;
            vec2 cell = fract( rp ) - 0.5;
            float dd = length( cell );
            float rad = 0.10 + 0.30 * amt;
            float dotv = 1.0 - smoothstep( rad - 0.09, rad + 0.09, dd );
            outgoingLight = mix( outgoingLight, uHalfColor, dotv * amt * 0.60 );
          }
        }
      }
      #include <opaque_fragment>
      `,
    );
  };

  // force a distinct program per configuration
  m.customProgramCacheKey = () =>
    `sph|${instanced ? 'i' : ''}${fx ? 'f' : ''}|${opts.grime ? 'g' : ''}|${opts.ramp ?? 'HARD3'}`;

  return m;
}

/**
 * Cached recipes. Two calls with the same arguments return the SAME material instance,
 * which is what lets bakeStatic() merge forty little meshes into one draw call.
 * Anything you intend to mutate per-object must use toon() directly.
 */
const _shared = new Map<string, ToonMaterial>();

function sharedToon(key: string, make: () => ToonMaterial): ToonMaterial {
  let m = _shared.get(key);
  if (!m) {
    m = make();
    m.userData.shared = true;
    _shared.set(key, m);
  }
  return m;
}

const texKey = (t?: Texture | null) => (t ? t.uuid : '-');

/** convenience: a big flat surface (floor/wall) — soft 2-band, no halftone */
export function toonSurface(color: number, grime?: Texture | null): ToonMaterial {
  return sharedToon(`surface|${color}|${texKey(grime)}`, () =>
    toon({ color, ramp: 'SOFT2', rim: 0.0, halftone: 0, grime, grimeStrength: grime ? 0.5 : 0, side: FrontSide }));
}

/** convenience: chassis — 3-band, grime mandatory, halftone on */
export function toonChassis(color: number, grime?: Texture | null): ToonMaterial {
  return sharedToon(`chassis|${color}|${texKey(grime)}`, () =>
    toon({ color, ramp: 'HARD3', rim: 0.35, halftone: 1.0, grime, grimeStrength: grime ? 0.42 : 0 }));
}

export function toonFelt(color: number): ToonMaterial {
  return sharedToon(`felt|${color}`, () => toon({ color, ramp: 'CLOTH3', rim: 0.12, halftone: 0.35 }));
}

export function toonMetal(color: number): ToonMaterial {
  return sharedToon(`metal|${color}`, () => toon({ color, ramp: 'METAL4', rim: 0.55, halftone: 0 }));
}

export function toonPlush(color: number): ToonMaterial {
  return sharedToon(`plush|${color}`, () => toon({ color, ramp: 'CLOTH3', rim: 0.5, halftone: 0.2 }));
}

/** a shared painted-plastic recipe (buttons, pockets, trim) */
export function toonPlastic(color: number, emissive = 0x000000, halftone = 0.5): ToonMaterial {
  return sharedToon(`plastic|${color}|${emissive}|${halftone}`, () =>
    toon({ color, ramp: 'HARD3', rim: 0.45, halftone, emissive }));
}

/** a shared dark-cavity recipe: the inside of every mouth in the arcade */
export function toonCavity(color: number): ToonMaterial {
  return sharedToon(`cavity|${color}`, () => toon({ color, ramp: 'SOFT2', rim: 0 }));
}

export function isToon(m: Material): m is ToonMaterial {
  return (m as ToonMaterial).sph !== undefined;
}

export { DoubleSide, Vector2 };
